// src/hr/portal/EmailChips.js -- a person's other sign-in addresses as removable chips.
// The text being typed (`draft`) belongs to the parent so a submit can include an address that was
// typed but not yet added. Enter or a comma adds it; Backspace on an empty box removes the last chip.
import React, { useId, useState } from 'react';
import { FiX } from 'react-icons/fi';
import { Btn } from '../kit';
import { cx, plainId } from '../kit/util';
import { EMAIL_RE } from './portalUtils';

const normal = (email) => String(email || '').trim().toLowerCase();

// The list with a valid, new draft added; { error } when the draft can't be added.
export function withDraft(emails, draft, primary) {
  const value = normal(draft);
  if (!value) return { emails };
  if (!EMAIL_RE.test(value)) return { emails, error: 'Enter a valid email' };
  if (emails.includes(value) || value === normal(primary)) return { emails, error: 'That address is already listed' };
  return { emails: [...emails, value] };
}

export function EmailChips({
  label = 'Other email addresses', hint, emails, onChange, draft, onDraftChange, primary, error: outsideError
}) {
  const id = `hrp-echips-${plainId(useId())}`;
  const [error, setError] = useState('');
  const shown = error || outsideError || '';

  const add = () => {
    const next = withDraft(emails, draft, primary);
    if (next.error) {
      setError(next.error);
      return;
    }
    if (next.emails !== emails) onChange(next.emails);
    onDraftChange('');
    setError('');
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      add();
    } else if (e.key === 'Backspace' && !draft && emails.length) {
      onChange(emails.slice(0, -1));
    }
  };

  return (
    <div className={cx('hrp-field', shown && 'hrp-field--error')}>
      <label htmlFor={id} className="hrp-field__label">{label}</label>
      <div className="hrp-echips">
        {emails.length > 0 && (
          <ul className="hrp-echips__list" aria-label="Addresses added">
            {emails.map((email) => (
              <li key={email} className="hrp-echip">
                <span className="hrp-echip__txt">{email}</span>
                <button type="button" className="hrp-iconbtn hrp-echip__x" aria-label={`Remove ${email}`} onClick={() => onChange(emails.filter((e) => e !== email))}>
                  <FiX aria-hidden="true" />
                </button>
              </li>
            ))}
          </ul>
        )}
        <div className="hrp-echips__add">
          <input
            id={id}
            className="hrp-input"
            type="email"
            value={draft}
            placeholder="name@solu-m.com"
            autoComplete="off"
            aria-invalid={shown ? 'true' : undefined}
            aria-describedby={[hint ? `${id}-hint` : null, shown ? `${id}-err` : null].filter(Boolean).join(' ') || undefined}
            onChange={(e) => { onDraftChange(e.target.value); if (error) setError(''); }}
            onKeyDown={onKeyDown}
          />
          <Btn variant="ghost" size="sm" onClick={add} disabled={!draft.trim()}>Add</Btn>
        </div>
      </div>
      {hint && <div id={`${id}-hint`} className="hrp-field__hint">{hint}</div>}
      {shown && <div id={`${id}-err`} className="hrp-field__err" role="alert">{shown}</div>}
    </div>
  );
}

export default EmailChips;
